import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CAlert, CButton, CCard, CCardBody, CCol, CLoadingButton, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowBottom,
  cilArrowTop,
  cilDollar,
  cilLayers,
  cilReload,
  cilTrash,
  cilWarning,
} from '@coreui/icons'
import Bootbox from 'bootbox-react'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import Loader from 'src/components/Loader'
import { InfoBox } from 'src/components/common/ImportWizard'
import DefaultSalarySymbol from './DefaultSalarySymbol'
import SetMinPlayerValue from './SetMinPlayerValue'
import SetMaxPlayerValue from './SetMaxPlayerValue'
import { formatValue, parseValue } from './valueFormat'
import './FantasyValue.css'

// A band shows this many chips before collapsing the rest behind "Show all".
const CHIP_PREVIEW = 12

const LadderBand = ({ tone, title, values, symbol }) => {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? values : values.slice(0, CHIP_PREVIEW)
  const hidden = values.length - shown.length

  return (
    <div className="fv-band">
      <div className="fv-band-heading">
        <span className={`fv-dot fv-dot--${tone}`} />
        <span className="fv-band-title">{title}</span>
        <span className="fv-band-count">{values.length}</span>
      </div>
      {values.length === 0 ? (
        <div className="fv-band-empty">Not extended</div>
      ) : (
        <div className="fv-chips">
          {shown.map((value) => (
            <span key={value} className={`fv-chip fv-chip--${tone}`}>
              {formatValue(value, symbol)}
            </span>
          ))}
          {(hidden > 0 || expanded) && values.length > CHIP_PREVIEW && (
            <button type="button" className="fv-chip-more" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : `+${hidden} more`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const Index = () => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const fetchData = useCallback(() => {
    setError(false)
    PlayerService.getPlayerFantasyValue()
      .then((result) => setData(result))
      .catch(() => {
        setError(true)
        ToastComponent('Could not load fantasy values', 'error')
      })
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const symbol = data?.default_salary_symbol ?? ''

  // Everything below is read straight off the API response; the default ladder is fixed
  // server-side and the min/max settings extend it below and above.
  const ladder = useMemo(() => {
    const defaults = (data?.default_value || []).map((item) => Number(item.value))
    const low = (data?.playerMinValues || []).map((item) => parseValue(item.price_name))
    const high = (data?.playerMaxValues || []).map((item) => parseValue(item.price_name))
    const sortDesc = (list) => [...list].sort((a, b) => b - a)
    return {
      low: sortDesc(low),
      defaults: sortDesc(defaults),
      high: sortDesc(high),
      defaultLow: defaults.length ? Math.min(...defaults) : null,
      defaultHigh: defaults.length ? Math.max(...defaults) : null,
    }
  }, [data])

  const all = [...ladder.low, ...ladder.defaults, ...ladder.high]
  const extendedCount = ladder.low.length + ladder.high.length

  const STAT_CARDS = [
    {
      label: 'Currency Symbol',
      value: symbol || 'None',
      icon: cilDollar,
      color: 'primary',
    },
    {
      label: 'Lowest Value',
      value: all.length ? formatValue(Math.min(...all), symbol) : '—',
      icon: cilArrowBottom,
      color: 'info',
    },
    {
      label: 'Highest Value',
      value: all.length ? formatValue(Math.max(...all), symbol) : '—',
      icon: cilArrowTop,
      color: 'success',
    },
    {
      label: 'Price Points',
      value: all.length,
      icon: cilLayers,
      color: 'secondary',
    },
  ]

  const resetExtensions = () => {
    setShowConfirm(false)
    setResetting(true)
    PlayerService.deletePlayerAdvanceValue()
      .then((result) => {
        ToastComponent(result.message, 'success')
        fetchData()
      })
      .catch(() => ToastComponent('Could not reset the extended values', 'error'))
      .finally(() => setResetting(false))
  }

  const header = (
    <div className="ge-page-header">
      <h1 className="ge-page-title">Fantasy Values</h1>
      <p className="ge-page-subtitle">
        Choose the currency symbol and the range of values players can be priced at.
      </p>
    </div>
  )

  if (error && !data) {
    return (
      <>
        {header}
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load the fantasy value settings.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchData}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      </>
    )
  }

  if (!data) {
    return (
      <>
        {header}
        <CCard>
          <CCardBody>
            <Loader />
          </CCardBody>
        </CCard>
      </>
    )
  }

  const bandTotal = all.length || 1

  return (
    <>
      {header}

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((s) => (
          <CCol sm={6} xl={3} key={s.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">{s.label}</div>
                  <div className="fs-4 fw-semibold">{s.value}</div>
                </div>
                <div className={`ge-stat-icon ge-stat-icon--${s.color}`}>
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CRow className="g-3">
        <CCol xs={12}>
          <CCard className="fv-card">
            <CCardBody>
              <div className="fv-card-heading">
                <div>
                  <h2 className="fv-card-title">Value Ladder</h2>
                  <p className="fv-card-note">
                    Every value a player can be priced at, from highest to lowest.
                  </p>
                </div>
              </div>

              <div className="fv-scale" aria-hidden="true">
                {ladder.high.length > 0 && (
                  <div
                    className="fv-scale-seg fv-scale-seg--success"
                    style={{ flexGrow: ladder.high.length / bandTotal }}
                  />
                )}
                <div
                  className="fv-scale-seg fv-scale-seg--primary"
                  style={{ flexGrow: ladder.defaults.length / bandTotal }}
                />
                {ladder.low.length > 0 && (
                  <div
                    className="fv-scale-seg fv-scale-seg--info"
                    style={{ flexGrow: ladder.low.length / bandTotal }}
                  />
                )}
              </div>
              <div className="fv-scale-labels">
                <span>{all.length ? formatValue(Math.max(...all), symbol) : ''}</span>
                <span>{all.length ? formatValue(Math.min(...all), symbol) : ''}</span>
              </div>

              <div className="fv-bands">
                <LadderBand
                  tone="success"
                  title="Extended high"
                  values={ladder.high}
                  symbol={symbol}
                />
                <LadderBand
                  tone="primary"
                  title="Default range"
                  values={ladder.defaults}
                  symbol={symbol}
                />
                <LadderBand tone="info" title="Extended low" values={ladder.low} symbol={symbol} />
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12}>
          <InfoBox className="mb-0">
            The default range ({formatValue(ladder.defaultLow, symbol)} to{' '}
            {formatValue(ladder.defaultHigh, symbol)}) is the same for every game. Use the minimum
            and maximum settings below to add extra values at either end, in steps of{' '}
            {formatValue(0.5, symbol)}.
          </InfoBox>
        </CCol>

        <CCol lg={4}>
          <DefaultSalarySymbol defaultSalarySymbol={symbol} fetchData={fetchData} />
        </CCol>
        <CCol md={6} lg={4}>
          <SetMinPlayerValue
            advanceMinPlayerprice={data.advance_min_player_price}
            defaultLow={ladder.defaultLow}
            symbol={symbol}
            fetchData={fetchData}
          />
        </CCol>
        <CCol md={6} lg={4}>
          <SetMaxPlayerValue
            advanceMaxPlayerprice={data.advance_max_player_price}
            defaultHigh={ladder.defaultHigh}
            symbol={symbol}
            fetchData={fetchData}
          />
        </CCol>

        <CCol xs={12}>
          <CCard className="fv-card fv-card--danger">
            <CCardBody className="d-flex flex-wrap align-items-center gap-3">
              <div className="flex-grow-1">
                <h2 className="fv-card-title">Reset extended values</h2>
                <p className="fv-card-note">
                  {extendedCount > 0
                    ? `Removes the ${extendedCount} extended value${
                        extendedCount !== 1 ? 's' : ''
                      } and clears both limits, leaving only the default range.`
                    : 'Nothing to reset - only the default range is in use.'}
                </p>
              </div>
              <CLoadingButton
                type="button"
                color="danger"
                variant="outline"
                loading={resetting}
                disabled={extendedCount === 0 && !resetting}
                onClick={() => setShowConfirm(true)}
              >
                <CIcon icon={cilTrash} className="me-1" />
                Reset to Default
              </CLoadingButton>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <Bootbox
        show={showConfirm}
        type={'confirm'}
        message={`Remove all ${extendedCount} extended values and clear both limits? Only the default range will remain.`}
        onSuccess={resetExtensions}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
      />
    </>
  )
}

export default Index
